import React from "react"
import { Box, HStack, Skeleton, Text } from "@chakra-ui/react"
import { useNavigate } from "react-router-dom"
import { FaPlus } from "react-icons/fa6"
import { Tooltip } from "../chakra/Tooltip"
import { useNavigation } from "../providers/NavigationProvider"
import { useSection } from "../providers/helpers/useRoute"
import {
  COLOR_ACCENT,
  COLOR_CONNECTION_HOVER,
  COLOR_CONNECTION_INACTIVE,
  DataConnectionState,
} from "./constants"
import {
  type DataConnectionAsset,
  type ConnectionOwnership,
  type AssignedSystemInfo,
  resolveDataConnectionState,
  resolveStateFromConnections,
  formatConnectionRelativeTime,
  formatConnectionTooltip,
  isHybridOrParentAsset,
  isChildModuleActive,
  getAssetAssignedSystem,
} from "./dataConnectionIndicatorUtils"
import { useDataConnectionStatus } from "./useDataConnectionStatus"

export {
  type DataConnectionAsset,
  type DataConnectionRecord,
  type ConnectionOwnership,
  type AssignedSystemInfo,
  resolveDataConnectionState,
  resolveStateFromConnections,
  formatConnectionRelativeTime,
  formatConnectionTooltip,
  isHybridOrParentAsset,
  isChildModuleActive,
  getAssetAssignedSystem,
} from "./dataConnectionIndicatorUtils"
export { useDataConnectionStatus } from "./useDataConnectionStatus"

export interface DataConnectionIndicatorProps {
  /** Explicit state override; if omitted, derived from `asset` */
  state?: DataConnectionState
  /** Asset entity used to compute state and default navigation url */
  asset?: DataConnectionAsset | null
  /** Explicit connection presence flag (e.g. from monitoringStatusData) */
  hasConnection?: boolean | null
  /** Optional last data timestamp string or Date */
  lastDataTimestamp?: string | Date | null
  /** Optional variant for inactive dot styling: solid (default) or outline */
  variant?: "solid" | "outline"
  /** Whether to render label text alongside the dot (e.g. for status card headers) */
  showLabel?: boolean
  /** Optional loading state override */
  loading?: boolean
  /** Optional system ID override if asset is in a system */
  systemId?: string | null
  /** Optional system name override */
  systemName?: string | null
  /** Optional click handler override for "Set up data connection" */
  onClickSetup?: (e: React.MouseEvent) => void
  /** Custom URL override for setup */
  setupUrl?: string
}

/**
 * Universal Data Connection Indicator component.
 *
 * Provides visual indication of data connection status (Active / Inactive / Not Configured)
 * with an interactive action to set up connection when unconfigured.
 */
export const DataConnectionIndicator = ({
  state: stateProp,
  asset,
  hasConnection,
  lastDataTimestamp,
  variant = "solid",
  showLabel = false,
  loading: loadingProp,
  systemId: systemIdProp,
  systemName: systemNameProp,
  onClickSetup,
  setupUrl,
}: DataConnectionIndicatorProps) => {
  let navigate: ReturnType<typeof useNavigate> | null = null
  try {
    navigate = useNavigate()
  } catch {
    navigate = null
  }

  const hubNavigate = useNavigation()
  let currentSection: string | undefined = undefined
  try {
    currentSection = useSection()
  } catch {
    currentSection = undefined
  }

  const {
    state,
    ownership,
    isLoading,
    targetUrl,
    relativeTimeLong,
    relativeTimeShort,
  } = useDataConnectionStatus({
    state: stateProp,
    asset,
    hasConnection,
    lastDataTimestamp,
    systemId: systemIdProp,
    systemName: systemNameProp,
    setupUrl,
    loading: loadingProp,
  })

  if (isLoading) {
    if (showLabel) {
      return (
        <HStack gap="9px" alignItems="center" data-testid="data-connection-skeleton">
          <Skeleton
            width="0.75rem"
            height="0.75rem"
            borderRadius="999px"
            flexShrink={0}
          />
          <Skeleton height="14px" width="100px" borderRadius="sm" />
        </HStack>
      )
    }

    return (
      <Skeleton
        width="0.75rem"
        height="0.75rem"
        borderRadius="999px"
        flexShrink={0}
        display="inline-block"
        data-testid="data-connection-skeleton"
      />
    )
  }

  const { tooltipTitle, labelText, ariaLabel } = formatConnectionTooltip({
    state,
    ownership,
    relativeTimeLong,
    relativeTimeShort,
  })

  const isCrossSection = Boolean(
    targetUrl &&
      targetUrl !== "#" &&
      ((targetUrl.startsWith("/systems") && currentSection !== "systems") ||
        (targetUrl.startsWith("/assets") && currentSection !== "assets"))
  )

  const handleSetupClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onClickSetup) {
      onClickSetup(e)
    } else if (targetUrl && targetUrl !== "#") {
      if (hubNavigate && isCrossSection) {
        hubNavigate(targetUrl)
      } else if (navigate) {
        navigate(targetUrl)
      } else if (typeof window !== "undefined") {
        window.location.href = targetUrl
      }
    }
  }

  const dotBaseStyle = {
    width: "0.75rem",
    height: "0.75rem",
    borderRadius: "999px",
    flexShrink: 0,
    boxSizing: "border-box" as const,
    cursor: "help",
  }

  // --- Active state ---
  if (state === DataConnectionState.Active) {
    const dot = (
      <Box
        as="span"
        {...(!showLabel ? { "aria-label": ariaLabel } : { "aria-hidden": "true" })}
        bg={COLOR_ACCENT}
        {...dotBaseStyle}
      />
    )

    if (!showLabel) {
      return (
        <Tooltip content={tooltipTitle} openDelay={150} closeDelay={100} showArrow>
          {dot}
        </Tooltip>
      )
    }

    return (
      <Tooltip content={tooltipTitle} openDelay={150} closeDelay={100} showArrow>
        <HStack gap="9px" alignItems="center" cursor="help" aria-label={ariaLabel}>
          {dot}
          <Text fontSize="13px" color="#545454" whiteSpace="nowrap">
            {labelText}
          </Text>
        </HStack>
      </Tooltip>
    )
  }

  // --- Inactive state ---
  if (state === DataConnectionState.Inactive) {
    const inactiveStyle =
      variant === "outline"
        ? {
            ...dotBaseStyle,
            bg: "#ffffff",
            border: `1.5px solid ${COLOR_CONNECTION_INACTIVE}`,
          }
        : {
            ...dotBaseStyle,
            bg: COLOR_CONNECTION_INACTIVE,
          }

    const dot = (
      <Box
        as="span"
        {...(!showLabel ? { "aria-label": ariaLabel } : { "aria-hidden": "true" })}
        {...inactiveStyle}
      />
    )

    if (!showLabel) {
      return (
        <Tooltip content={tooltipTitle} openDelay={150} closeDelay={100} showArrow>
          {dot}
        </Tooltip>
      )
    }

    return (
      <Tooltip content={tooltipTitle} openDelay={150} closeDelay={100} showArrow>
        <HStack gap="9px" alignItems="center" cursor="help" aria-label={ariaLabel}>
          {dot}
          <Text fontSize="13px" color="#545454" whiteSpace="nowrap">
            {labelText}
          </Text>
        </HStack>
      </Tooltip>
    )
  }

  // --- Not Configured state (clickable) ---
  if (showLabel) {
    return (
      <Tooltip content={tooltipTitle} openDelay={150} closeDelay={100} showArrow>
        <HStack
          as="a"
          href={targetUrl}
          onClick={handleSetupClick}
          gap="9px"
          alignItems="center"
          cursor="pointer"
          textDecoration="none"
          className="group"
          data-group
          aria-label={ariaLabel}
          _hover={{ textDecoration: "none" }}
        >
          <Box
            as="span"
            width="0.75rem"
            height="0.75rem"
            p="0"
            borderRadius="999px"
            bg="transparent"
            border={`1.5px dashed ${COLOR_ACCENT}`}
            boxSizing="border-box"
            flexShrink={0}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            color={COLOR_ACCENT}
            transition="all 0.15s ease"
            _groupHover={{
              bg: COLOR_CONNECTION_HOVER,
              border: `1.5px solid ${COLOR_CONNECTION_HOVER}`,
              color: "#ffffff",
            }}
          >
            <FaPlus style={{ fontSize: "0.375rem", strokeWidth: "1px" }} />
          </Box>
          <Text
            fontSize="13px"
            color={COLOR_ACCENT}
            whiteSpace="nowrap"
            transition="all 0.15s ease"
            _groupHover={{
              color: COLOR_CONNECTION_HOVER,
              textDecoration: "underline",
            }}
          >
            {labelText}
          </Text>
        </HStack>
      </Tooltip>
    )
  }

  const button = (
    <Box
      as="button"
      type="button"
      aria-label={ariaLabel}
      onClick={handleSetupClick}
      width="0.75rem"
      height="0.75rem"
      p="0"
      borderRadius="999px"
      bg="transparent"
      border={`1.5px dashed ${COLOR_ACCENT}`}
      boxSizing="border-box"
      flexShrink={0}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      color={COLOR_ACCENT}
      cursor="pointer"
      transition="all 0.15s ease"
      _hover={{
        bg: COLOR_CONNECTION_HOVER,
        border: `1.5px solid ${COLOR_CONNECTION_HOVER}`,
        color: "#ffffff",
      }}
    >
      <FaPlus style={{ fontSize: "0.375rem", strokeWidth: "1px" }} />
    </Box>
  )

  return (
    <Tooltip content={tooltipTitle} openDelay={150} closeDelay={100} showArrow>
      {button}
    </Tooltip>
  )
}

export default DataConnectionIndicator
